/**
 * PageTransition — V1.9.9
 *
 * Wraps a nested-route `<Outlet />` in Framer Motion's <AnimatePresence>
 * so page-to-page navigation fades + slides like a native mobile screen
 * push. Only the Outlet child re-mounts — the surrounding shell (sidebar,
 * top bar, DaddyTalk drawer) stays static and never flashes.
 *
 * Keyed on `location.pathname` so tab-swaps that change ONLY the query
 * (e.g. `/messages?tab=team` vs `?tab=daddytalk`) don't re-mount the tree.
 *
 * Respects `prefers-reduced-motion: reduce` for accessibility.
 */

import React from "react";
import { Outlet, useLocation } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";

const reduceMotion = () =>
    typeof window !== "undefined" &&
    window.matchMedia &&
    window.matchMedia("(prefers-reduced-motion: reduce)").matches;

export default function PageTransition() {
    const location = useLocation();
    const reduce = reduceMotion();
    return (
        <AnimatePresence mode="wait" initial={false}>
            <motion.div
                key={location.pathname}
                initial={reduce ? false : { opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                exit={reduce ? { opacity: 0 } : { opacity: 0, y: -6 }}
                transition={{
                    duration: reduce ? 0.001 : 0.22,
                    ease: [0.22, 0.61, 0.36, 1],
                }}
                style={{ flex: 1, minHeight: 0, display: "flex", flexDirection: "column" }}
            >
                <Outlet />
            </motion.div>
        </AnimatePresence>
    );
}
